import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { Footer, Navbar } from "../components";
import { useSelector, useDispatch } from "react-redux";
import { setCart } from "../redux/action";
import { Link, useNavigate } from "react-router-dom";
import api from "../utils/base_url";
import { clearCart } from "../redux/reducer/handleCart";
import { AuthContext } from "../context/AuthContext";
import toast from "react-hot-toast";
import "../styles/index.css";
import { PaymentContext } from "../context/PaymentContext";
import { useRef } from "react";

const Checkout = () => {
  const cartItems = useSelector((state) => state.handleCart);
  const { user } = useContext(AuthContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fieldRefs = useRef({});

  const { setPendingOrderId } = useContext(PaymentContext);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    contact_number: "",
    secondary_number: "",

    billing_door: "",
    billing_street: "",
    billing_area: "",
    billing_landmark: "",
    billing_city: "",
    billing_state: "",
    billing_pincode: "",

    shipping_door: "",
    shipping_street: "",
    shipping_area: "",
    shipping_landmark: "",
    shipping_city: "",
    shipping_state: "",
    shipping_pincode: "",
    sameAsBilling: false,
    paymentMethod: "online",
    preferredCourier: "",
    billing_manual_entry: false,
    shipping_manual_entry: false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // ---------------------------------------------------------
  // Fetch cart preview (prices, GST%, shipping, totals) from
  // the backend — single source of truth, shared with Cart page.
  // ---------------------------------------------------------
  useEffect(() => {
    if (!cartItems.length) {
      setPreview(null);
      setPreviewLoading(false);
      return;
    }

    let cancelled = false;

    const fetchPreview = async () => {
      setPreviewLoading(true);
      try {
        const res = await api.post("cart/preview/", {
          items: cartItems.map((item) => ({ product_id: item.id, qty: item.qty })),
        });
        if (!cancelled) setPreview(res.data);
      } catch (error) {
        console.error("Checkout preview error:", error);
        if (!cancelled) toast.error("Unable to calculate order totals");
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };

    fetchPreview();

    return () => {
      cancelled = true;
    };
  }, [cartItems]);

  const fetchAddressFromPincode = async (pincode, type) => {
    if (!/^\d{6}$/.test(pincode)) return;

    try {
      const response = await fetch(
        `https://pincode.scaleupstores.com/api/v1/pincode/lookup/${pincode}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-API-KEY": "my_secure_api_key_2026",
          },
        }
      );

      const data = await response.json();

      if (data.status) {
        setFormData((prev) => ({
          ...prev,
          [`${type}_city`]: data.district || "",
          [`${type}_state`]: data.state || "",
          [`${type}_manual_entry`]: false,
        }));

        setErrors((prev) => ({
          ...prev,
          [`${type}_pincode`]: "",
          [`${type}_city`]: "",
          [`${type}_state`]: "",
        }));

        return;
      }

      toast.error("Pincode not found. Please enter manually.");

      setFormData((prev) => ({
        ...prev,
        [`${type}_city`]: "",
        [`${type}_state`]: "",
        [`${type}_manual_entry`]: true,
      }));
    } catch (error) {
      console.error("Pincode API error:", error);

      toast.error("Pincode service unavailable. Please enter manually.");

      setFormData((prev) => ({
        ...prev,
        [`${type}_city`]: "",
        [`${type}_state`]: "",
        [`${type}_manual_entry`]: true,
      }));
    }
  };

  const parseAddress = (address = "") => {
    const parts = address.split(",").map((p) => p.trim());

    let city = "";
    let state = "";
    let pincode = "";

    parts.forEach((p) => {
      const match = p.match(/(.+)\s*-\s*(\d{6})/);
      if (match) {
        state = match[1].trim();
        pincode = match[2];
      }
    });

    const pinIndex = parts.findIndex((p) => p.includes(pincode));
    if (pinIndex > 0) {
      city = parts[pinIndex - 1];
    }

    return {
      door: parts[0] || "",
      street: parts[1] || "",
      area: parts[2] || "",
      landmark:
        parts
          .find((p) => p.toLowerCase().startsWith("landmark"))
          ?.replace("Landmark:", "")
          .trim() || "",
      city,
      state,
      pincode,
    };
  };

  const validateCheckout = useCallback(async () => {
    try {
      const res = await api.post("checkout-validate/", { user_id: user.id });

      if (!res.data.status) {
        const qtyMap = {};
        res.data.valid_items.forEach((i) => (qtyMap[i.id] = i.qty));

        const newCart = cartItems
          .map((item) =>
            qtyMap[item.id] !== undefined
              ? { ...item, qty: qtyMap[item.id] }
              : null,
          )
          .filter(Boolean);

        dispatch(setCart(newCart));

        toast.error("Cart updated due to stock/reservation changes");
        navigate("/cart");
        return;
      }
    } catch (err) {
      toast.error("Unable to validate checkout");
      navigate("/cart");
    }
  }, [cartItems, dispatch, navigate, user.id]);

  useEffect(() => {
    validateCheckout();
  }, [validateCheckout]);

  useEffect(() => {
    if (!user?.customer_details?.id) return;

    (async () => {
      try {
        const res = await api.get(
          `/customerslist/${user.customer_details.id}/`,
        );
        const data = res.data.data;

        if (!data.address) return;

        const parsed = parseAddress(data.address);

        setFormData((prev) => ({
          ...prev,
          first_name: data.full_name?.split(" ")[0] || "",
          last_name: data.full_name?.split(" ").slice(1).join(" ") || "",
          contact_number: data.contact_number || "",
          secondary_number: data.secondary_number || "",

          billing_door: parsed.door,
          billing_street: parsed.street,
          billing_area: parsed.area,
          billing_landmark: parsed.landmark,
          billing_city: parsed.city,
          billing_state: parsed.state,
          billing_pincode: parsed.pincode,

          shipping_door: parsed.door,
          shipping_street: parsed.street,
          shipping_area: parsed.area,
          shipping_landmark: parsed.landmark,
          shipping_city: parsed.city,
          shipping_state: parsed.state,
          shipping_pincode: parsed.pincode,
        }));
      } catch (err) {
        console.error("Failed to load saved address", err);
      }
    })();
  }, [user]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    if (
      (name === "contact_number" || name === "secondary_number") &&
      value !== "" &&
      !/^\d+$/.test(value)
    ) {
      return;
    }

    if (name === "preferredCourier") {
      setFormData((prev) => ({
        ...prev,
        preferredCourier: String(value),
      }));

      setErrors((prev) => ({ ...prev, preferredCourier: "" }));
      return;
    }

    if (name === "sameAsBilling") {
      setFormData((prev) => ({
        ...prev,
        sameAsBilling: checked,
        shipping_door: checked ? prev.billing_door : "",
        shipping_street: checked ? prev.billing_street : "",
        shipping_area: checked ? prev.billing_area : "",
        shipping_landmark: checked ? prev.billing_landmark : "",
        shipping_city: checked ? prev.billing_city : "",
        shipping_state: checked ? prev.billing_state : "",
        shipping_pincode: checked ? prev.billing_pincode : "",
        shipping_manual_entry: checked
          ? prev.billing_manual_entry
          : false,
      }));
    } else if (name === "billingAddress") {
      setFormData((prev) => ({
        ...prev,
        billingAddress: value,
        ...(prev.sameAsBilling ? { shippingAddress: value } : {}),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ---------------------------------------------------------
  // Totals now come straight from the backend preview response
  // instead of being computed here.
  // ---------------------------------------------------------
  const totals = preview?.totals;
  const subtotal = totals?.subtotal ?? 0;
  const gstPercent = totals?.gst_percent ?? 0;
  const gstAmount = totals?.gst_amount ?? 0;
  const shippingCost = totals?.shipping_cost ?? 0;
  const totalAmount = totals?.total_amount ?? 0;
  const totalItems = totals?.total_items ?? cartItems.reduce((acc, item) => acc + item.qty, 0);

  const validateForm = () => {
    const errors = {};

    if (!formData.first_name.trim()) {
      errors.first_name = "First name is required";
    } else if (!/^[A-Za-z ]{2,30}$/.test(formData.first_name)) {
      errors.first_name = "Enter a valid first name";
    }

    if (
      formData.last_name &&
      !/^[A-Za-z ]{1,30}$/.test(formData.last_name)
    ) {
      errors.last_name = "Enter a valid last name";
    }

    if (!/^[6-9]\d{9}$/.test(formData.contact_number)) {
      errors.contact_number = "Enter valid 10-digit mobile number";
    }

    if (
      formData.secondary_number &&
      !/^[6-9]\d{9}$/.test(formData.secondary_number)
    ) {
      errors.secondary_number = "Enter valid secondary number";
    }

    if (!formData.billing_door.trim())
      errors.billing_door = "House / Door No is required";

    if (
      !formData.billing_street.trim() ||
      formData.billing_street.length < 3
    )
      errors.billing_street = "Street must be at least 3 characters";

    if (
      !formData.billing_area.trim() ||
      formData.billing_area.length < 3
    )
      errors.billing_area = "Area must be at least 3 characters";

    if (
      formData.billing_landmark &&
      formData.billing_landmark.length < 3
    )
      errors.billing_landmark = "Landmark must be at least 3 characters";

    if (!/^\d{6}$/.test(formData.billing_pincode))
      errors.billing_pincode = "Enter valid 6-digit pincode";

    if (!formData.billing_city.trim())
      errors.billing_city = "City is required";

    if (!formData.billing_state.trim())
      errors.billing_state = "State is required";

    if (!formData.sameAsBilling) {
      if (!formData.shipping_door.trim())
        errors.shipping_door = "House / Door No is required";

      if (
        !formData.shipping_street.trim() ||
        formData.shipping_street.length < 3
      )
        errors.shipping_street = "Street must be at least 3 characters";

      if (
        !formData.shipping_area.trim() ||
        formData.shipping_area.length < 3
      )
        errors.shipping_area = "Area must be at least 3 characters";

      if (
        formData.shipping_landmark &&
        formData.shipping_landmark.length < 3
      )
        errors.shipping_landmark = "Landmark must be at least 3 characters";

      if (!/^\d{6}$/.test(formData.shipping_pincode))
        errors.shipping_pincode = "Enter valid 6-digit pincode";

      if (!formData.shipping_city.trim())
        errors.shipping_city = "City is required";

      if (!formData.shipping_state.trim())
        errors.shipping_state = "State is required";
    }

    if (!formData.preferredCourier)
      errors.preferredCourier = "Please select a courier service";

    return errors;
  };

  const billingFull = [
    formData.billing_door,
    formData.billing_street,
    formData.billing_area,
    formData.billing_landmark && `Landmark: ${formData.billing_landmark}`,
    `${formData.billing_city}, ${formData.billing_state} - ${formData.billing_pincode}`,
    "India",
  ]
    .filter(Boolean)
    .join(", ");

  const shippingFull = formData.sameAsBilling
    ? billingFull
    : [
      formData.shipping_door,
      formData.shipping_street,
      formData.shipping_area,
      formData.shipping_landmark && `Landmark: ${formData.shipping_landmark}`,
      `${formData.shipping_city}, ${formData.shipping_state} - ${formData.shipping_pincode}`,
      "India",
    ]
      .filter(Boolean)
      .join(", ");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];
      const field = fieldRefs.current[firstErrorKey];

      if (field && !field.disabled) {
        setTimeout(() => {
          field.scrollIntoView({ block: "center" });
          field.focus();
        }, 0);
      }

      toast.error("Please fix the highlighted fields");
      return;
    }

    if (!cartItems.length) {
      toast.error("Cart is empty!");
      return;
    }

    if (!preview) {
      toast.error("Still calculating totals, please wait a moment");
      return;
    }

    // Build order payload
    // NOTE: subtotal/tax_percent/shipping_cost/total_amount below are
    // for display/reference only — the backend (ReserveOrderAPIView /
    // CreateCODOrderAPIView / OrderDetailsSerializer) always recomputes
    // these itself from product + offer + GST + shipping settings, so
    // the client-sent values are never trusted for the actual charge.
    const itemsPayload = cartItems.map((item) => ({
      product: item.id,
      quantity: item.qty,
    }));

    const orderPayload = {
      customer: user.customer_details.id,
      first_name: formData.first_name,
      last_name: formData.last_name,
      contact_number: formData.contact_number,
      secondary_number: formData.secondary_number,
      preferred_courier_service: formData.preferredCourier,
      billing_address: billingFull,
      shipping_address: shippingFull,
      payment_method: formData.paymentMethod,
      items: itemsPayload,
      subtotal,
      tax_percent: gstPercent,
      shipping_cost: shippingCost,
      total_amount: totalAmount,
    };

    // ===============================================================
    // 🧾 1️⃣ CASH ON DELIVERY
    // ===============================================================
    if (formData.paymentMethod === "cod") {
      try {
        const res = await api.post("orders/create-cod/", {
          order_data: orderPayload,
        });
        toast.success("Order placed successfully!");
        dispatch(clearCart());
        navigate(`/order-confirmation/${res.data.order.id}`);
      } catch (err) {
        const msg = err.response?.data?.message || "Something went wrong!";
        toast.error(msg);
      }
      return;
    }

    // ===============================================================
    // 💳 2️⃣ ONLINE PAYMENT FLOW (safe)
    // ===============================================================
    try {
      const reserveRes = await api.post("orders/reserve/", {
        items: itemsPayload,
        payment_method: "online",
        order_data: orderPayload,
      });

      if (!reserveRes.data.status) {
        toast.error(reserveRes.data.message);
        return;
      }

      const razorpayOrder = reserveRes.data.razorpay_order;
      localStorage.setItem("pendingOrder", razorpayOrder.id);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        order_id: razorpayOrder.id,
        name: "Village Kannama",
        description: "Order Payment",

        handler: function (response) {
          setProcessingPayment(false);
          toast.success("Payment successful! Confirming your order…");
          setPendingOrderId(razorpayOrder.id);
        },

        modal: {
          ondismiss: function (reason) {
            if (reason === undefined) {
              api
                .post("payments/log-frontend-event/", {
                  razorpay_order_id: razorpayOrder.id,
                  event_type: "USER_CANCELLED",
                  error_details: { timestamp: new Date().toISOString() },
                })
                .catch((err) =>
                  console.log("⚠️ Failed to log cancellation:", err),
                );

              localStorage.removeItem("pendingOrder");
              setProcessingPayment(false);
              toast.error("⚠️ Payment cancelled.");
            } else if (reason === "timeout") {
              api
                .post("payments/log-frontend-event/", {
                  razorpay_order_id: razorpayOrder.id,
                  event_type: "PAYMENT_TIMEOUT",
                  error_details: { timestamp: new Date().toISOString() },
                })
                .catch((err) => console.log("⚠️ Failed to log timeout:", err));

              localStorage.removeItem("pendingOrder");
              toast.error("Payment timed out. Please try again.");
            } else {
              toast.error("Payment failed. Please try again.");
            }

            setProcessingPayment(false);
          },
        },

        prefill: {
          name: `${formData.first_name} ${formData.last_name}`,
          contact: formData.contact_number,
        },
        theme: { color: "#198754" },
      };

      setProcessingPayment(true);

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", (response) => {
        try {
          rzp.close();
        } catch (err) {
          console.error("❌ [CLOSE FAILED] Could not close modal:", err);
        }

        api
          .post("payments/log-frontend-event/", {
            razorpay_order_id: response.error.metadata.order_id,
            event_type: "PAYMENT_FAILED_FRONTEND",
            error_details: {
              payment_id: response.error.metadata.payment_id,
              error_code: response.error.code,
              error_description: response.error.description,
              error_reason: response.error.reason,
              timestamp: new Date().toISOString(),
            },
          })
          .catch((err) =>
            console.log("⚠️ Failed to log payment failure:", err),
          );

        const errorMsg = response.error.description || "Payment failed";
        toast.error(errorMsg);
        setProcessingPayment(false);
      });
      rzp.open();
    } catch (err) {
      const msg = err.response?.data?.message || "Unable to initiate payment";
      toast.error(msg);
    }
  };

  if (!cartItems.length) {
    return (
      <div className="container py-5 text-center">
        <h4 className="text-theme-dark">No items in Cart</h4>
        <Link to="/" className="btn btn-outline-themed mt-3">
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="container py-5">
        <div className="row g-4">
          {/* Order Summary */}
          <div className="col-lg-4 col-md-5 order-md-last">
            <div className="card shadow-theme rounded-theme">
              <div className="card-header bg-light">
                <h5 className="m-0 text-theme-dark">Order Summary</h5>
              </div>
              <div className="card-body">
                {previewLoading && !preview ? (
                  <div className="text-center py-3 text-theme-dark">
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Calculating totals...
                  </div>
                ) : (
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item d-flex justify-content-between">
                      Products ({totalItems}) <strong>₹{subtotal}</strong>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                      Shipping <strong>₹{shippingCost}</strong>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                      GST ({gstPercent}%) <strong>₹{gstAmount.toFixed ? gstAmount.toFixed(2) : gstAmount}</strong>
                    </li>

                    <li className="list-group-item d-flex justify-content-between text-theme-dark fw-bold">
                      Total <strong>₹{totalAmount}</strong>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Billing & Shipping Form */}
          <div className="col-lg-8 col-md-7">
            <div className="card shadow-theme rounded-theme">
              <div className="card-header bg-light">
                <h5 className="m-0 text-theme-dark">Billing & Shipping</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit} className="checkout-form">
                  <div className="row g-3">
                    {/* First Name */}
                    <div
                      ref={(el) => (fieldRefs.current.first_name = el)}
                      className="col-sm-6">
                      <label className="form-label required">First Name</label>
                      <input
                        type="text"
                        name="first_name"
                        maxLength="15"
                        className={`form-control themed-input ${errors.first_name ? "is-invalid" : ""
                          }`}
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                      />
                      {errors.first_name && (
                        <div className="error-text">{errors.first_name}</div>
                      )}
                    </div>

                    {/* Last Name */}
                    <div className="col-sm-6">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        name="last_name"
                        maxLength="15"
                        className={`form-control themed-input ${errors.last_name ? "is-invalid" : ""
                          }`}
                        value={formData.last_name}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Phone */}
                    <div
                      ref={(el) => (fieldRefs.current.contact_number = el)}
                      className="col-sm-6">
                      <label className="form-label required">
                        Contact Number
                      </label>
                      <input
                        type="tel"
                        name="contact_number"
                        maxLength="10"
                        className={`form-control themed-input ${errors.contact_number ? "is-invalid" : ""
                          }`}
                        value={formData.contact_number}
                        onChange={handleChange}
                        required
                      />
                      {errors.contact_number && (
                        <div className="error-text">
                          {errors.contact_number}
                        </div>
                      )}
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label">
                        Secondary Contact Number
                      </label>
                      <input
                        type="tel"
                        name="secondary_number"
                        minLength="10"
                        maxLength="10"
                        className={`form-control themed-input ${errors.secondary_number ? "is-invalid" : ""
                          }`}
                        value={formData.secondary_number}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Preferred Courier */}
                    <div
                      ref={(el) => (fieldRefs.current.preferredCourier = el)}
                      className="col-sm-6">
                      <label className="form-label required">
                        Preferred Courier
                      </label>
                      <select
                        className={`form-select themed-select ${errors.preferredCourier ? "is-invalid" : ""
                          }`}
                        name="preferredCourier"
                        value={formData.preferredCourier}
                        onChange={handleChange}
                      >
                        <option value="" disabled hidden>
                          Select Courier
                        </option>

                        <option value="Professional courier">
                          Professional Courier
                        </option>
                        <option value="ST courier">ST Courier</option>
                        <option value="India Post">India Post</option>
                      </select>
                      {errors.preferredCourier && (
                        <div className="error-text">
                          {errors.preferredCourier}
                        </div>
                      )}
                    </div>

                    {/* Billing Address */}
                    <h5>Billing Address</h5>

                    <div
                      ref={(el) => (fieldRefs.current.billing_door = el)}
                      className="col-sm-6">
                      <label className="form-label required">
                        House / Flat / Door No
                      </label>
                      <input
                        type="text"
                        name="billing_door"
                        maxLength="30"
                        className={`form-control themed-input ${errors.billing_door ? "is-invalid" : ""}`}
                        value={formData.billing_door}
                        onChange={handleChange}
                        required
                      />
                      {errors.billing_door && (
                        <div className="error-text">{errors.billing_door}</div>
                      )}
                    </div>

                    <div
                      ref={(el) => (fieldRefs.current.billing_street = el)}
                      className="col-sm-6">
                      <label className="form-label required">
                        Street / Road Name
                      </label>
                      <input
                        type="text"
                        name="billing_street"
                        maxLength="50"
                        className={`form-control themed-input ${errors.billing_street ? "is-invalid" : ""}`}
                        value={formData.billing_street}
                        onChange={handleChange}
                        required
                      />
                      {errors.billing_street && (
                        <div className="error-text">
                          {errors.billing_street}
                        </div>
                      )}
                    </div>

                    <div
                      ref={(el) => (fieldRefs.current.billing_area = el)}
                      className="col-sm-6">
                      <label className="form-label required">
                        Area / Locality
                      </label>
                      <input
                        type="text"
                        name="billing_area"
                        maxLength="50"
                        className={`form-control themed-input ${errors.billing_area ? "is-invalid" : ""}`}
                        value={formData.billing_area}
                        onChange={handleChange}
                        required
                      />
                      {errors.billing_area && (
                        <div className="error-text">{errors.billing_area}</div>
                      )}
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label">Landmark</label>
                      <input
                        type="text"
                        name="billing_landmark"
                        maxLength="50"
                        className={`form-control themed-input ${errors.billing_landmark ? "is-invalid" : ""}`}
                        value={formData.billing_landmark}
                        onChange={handleChange}
                      />
                      {errors.billing_landmark && (
                        <div className="error-text">
                          {errors.billing_landmark}
                        </div>
                      )}
                    </div>

                    <div
                      ref={(el) => (fieldRefs.current.billing_pincode = el)}
                      className="col-sm-6">
                      <label className="form-label required">Pincode</label>
                      <input
                        type="text"
                        name="billing_pincode"
                        minLength="6"
                        maxLength="6"
                        className={`form-control themed-input ${errors.billing_pincode ? "is-invalid" : ""}`}
                        value={formData.billing_pincode}
                        onChange={(e) => {
                          const value = e.target.value;

                          if (!/^\d*$/.test(value)) return;

                          setFormData((prev) => ({
                            ...prev,
                            billing_pincode: value,
                            billing_city: "",
                            billing_state: "",
                            billing_manual_entry: false,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            billing_pincode: "",
                            billing_city: "",
                            billing_state: "",
                          }));

                          if (value.length === 6) {
                            fetchAddressFromPincode(value, "billing");
                          }
                        }}
                        required
                      />

                      {errors.billing_pincode && (
                        <div className="error-text">
                          {errors.billing_pincode}
                        </div>
                      )}
                    </div>

                    <div ref={(el) => (fieldRefs.current.billing_city = el)}
                      className="col-sm-6">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        maxLength="25"
                        name="billing_city"
                        className={`form-control themed-input ${errors.billing_city ? "is-invalid" : ""}`}
                        value={formData.billing_city}
                        onChange={handleChange}
                        disabled={!formData.billing_manual_entry}
                      />
                      {errors.billing_city && (
                        <div className="error-text">
                          {errors.billing_city}
                        </div>
                      )}
                    </div>

                    <div ref={(el) => (fieldRefs.current.billing_state = el)}
                      className="col-sm-6">
                      <label className="form-label">State</label>
                      <input
                        type="text"
                        maxLength="25"
                        name="billing_state"
                        className={`form-control themed-input ${errors.billing_state ? "is-invalid" : ""}`}
                        value={formData.billing_state}
                        onChange={handleChange}
                        disabled={!formData.billing_manual_entry}
                      />
                      {errors.billing_state && (
                        <div className="error-text">
                          {errors.billing_state}
                        </div>
                      )}
                    </div>

                    {/* Same as Billing */}
                    <div className="col-12 form-check my-2">
                      <input
                        type="checkbox"
                        className="form-check-input thick-checkbox"
                        name="sameAsBilling"
                        checked={formData.sameAsBilling}
                        onChange={handleChange}
                        id="sameAsBilling"
                      />
                      <label
                        htmlFor="sameAsBilling"
                        className="form-check-label ms-2"
                      >
                        Shipping address same as billing
                      </label>

                      <style>{`
    .thick-checkbox {
      width: 20px;
      height: 20px;
      border: 2.5px solid #198754 !important;
      cursor: pointer;
      transition: all 0.25s ease;
    }

    .thick-checkbox:checked {
      background-color: #198754 !important;
      border-color: #198754 !important;
    }

    .thick-checkbox:focus {
      box-shadow: 0 0 0 0.15rem rgba(25, 135, 84, 0.25);
    }

    .form-check-label {
      font-size: 0.95rem;
      color: #000;
      cursor: pointer;
    }

    @media (max-width: 768px) {
      .thick-checkbox {
        width: 18px;
        height: 18px;
        border-width: 2px !important;
      }
    }
  `}</style>
                    </div>

                    {/* Shipping Address */}
                    {!formData.sameAsBilling && (
                      <>
                        <h5>Shipping Address</h5>
                        <div
                          ref={(el) => (fieldRefs.current.shipping_door = el)}
                          className="col-sm-6">
                          <label className="form-label required">
                            House / Flat / Door No
                          </label>
                          <input
                            type="text"
                            name="shipping_door"
                            className={`form-control themed-input ${errors.shipping_door ? "is-invalid" : ""}`}
                            value={formData.shipping_door}
                            minLength="3"
                            maxLength="30"
                            onChange={handleChange}
                            required
                          />
                          {errors.shipping_door && (
                            <div className="error-text">
                              {errors.shipping_door}
                            </div>
                          )}
                        </div>

                        <div
                          ref={(el) => (fieldRefs.current.shipping_street = el)}
                          className="col-sm-6">
                          <label className="form-label required">
                            Street / Road Name
                          </label>
                          <input
                            type="text"
                            name="shipping_street"
                            className={`form-control themed-input ${errors.shipping_street ? "is-invalid" : ""}`}
                            value={formData.shipping_street}
                            minLength="3"
                            maxLength="50"
                            onChange={handleChange}
                            required
                          />
                          {errors.shipping_street && (
                            <div className="error-text">
                              {errors.shipping_street}
                            </div>
                          )}
                        </div>

                        <div
                          ref={(el) => (fieldRefs.current.shipping_area = el)}
                          className="col-sm-6">
                          <label className="form-label required">
                            Area / Locality
                          </label>
                          <input
                            type="text"
                            name="shipping_area"
                            className={`form-control themed-input ${errors.shipping_area ? "is-invalid" : ""}`}
                            maxLength="50"
                            value={formData.shipping_area}
                            onChange={handleChange}
                            required
                          />
                          {errors.shipping_area && (
                            <div className="error-text">
                              {errors.shipping_area}
                            </div>
                          )}
                        </div>

                        <div className="col-sm-6">
                          <label className="form-label">Landmark</label>
                          <input
                            type="text"
                            name="shipping_landmark"
                            className={`form-control themed-input ${errors.shipping_landmark ? "is-invalid" : ""}`}
                            maxLength="50"
                            value={formData.shipping_landmark}
                            onChange={handleChange}
                          />
                          {errors.shipping_landmark && (
                            <div className="error-text">
                              {errors.shipping_landmark}
                            </div>
                          )}
                        </div>

                        <div
                          ref={(el) => (fieldRefs.current.shipping_pincode = el)}
                          className="col-sm-6">
                          <label className="form-label required">Pincode</label>
                          <input
                            type="text"
                            name="shipping_pincode"
                            minLength="6"
                            maxLength="6"
                            className={`form-control themed-input ${errors.shipping_pincode ? "is-invalid" : ""}`}
                            value={formData.shipping_pincode}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (!/^\d*$/.test(value)) return;

                              setFormData((prev) => ({
                                ...prev,
                                shipping_pincode: value,
                                shipping_city: "",
                                shipping_state: "",
                                shipping_manual_entry: false,
                              }));

                              setErrors((prev) => ({
                                ...prev,
                                shipping_pincode: "",
                                shipping_city: "",
                                shipping_state: "",
                              }));

                              if (value.length === 6) {
                                fetchAddressFromPincode(value, "shipping");
                              }
                            }}
                            required
                          />

                          {errors.shipping_pincode && (
                            <div className="error-text">
                              {errors.shipping_pincode}
                            </div>
                          )}
                        </div>

                        <div ref={(el) => (fieldRefs.current.shipping_city = el)}
                          className="col-sm-6">
                          <label className="form-label">City</label>
                          <input
                            type="text"
                            maxLength="25"
                            name="shipping_city"
                            className={`form-control themed-input ${errors.shipping_city ? "is-invalid" : ""}`}
                            value={formData.shipping_city}
                            onChange={handleChange}
                            disabled={!formData.shipping_manual_entry}
                          />

                          {errors.shipping_city && (
                            <div className="error-text">
                              {errors.shipping_city}
                            </div>
                          )}
                        </div>

                        <div ref={(el) => (fieldRefs.current.shipping_state = el)}
                          className="col-sm-6">
                          <label className="form-label">State</label>
                          <input
                            type="text"
                            maxLength="25"
                            name="shipping_state"
                            className={`form-control themed-input ${errors.shipping_state ? "is-invalid" : ""}`}
                            value={formData.shipping_state}
                            onChange={handleChange}
                            disabled={!formData.shipping_manual_entry}
                          />

                          {errors.shipping_state && (
                            <div className="error-text">
                              {errors.shipping_state}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  <hr className="my-4" />
                  <button
                    type="submit"
                    className="w-100 btn-themed btn-lg d-flex justify-content-center align-items-center"
                    disabled={processingPayment || previewLoading || !preview}
                  >
                    {processingPayment ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Processing Payment...
                      </>
                    ) : previewLoading || !preview ? (
                      "Calculating totals..."
                    ) : (
                      `Place Order (₹${totalAmount})`
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <style>{`
        :root {
          --brown-dark: #198754;
          --brown-light: #f1e6d4;
          --cream-bg: #fffaf4;
          --error-color: #b3261e;
        }

        .error-text {
          color: var(--error-color);
          font-size: 0.85rem;
          margin-top: 2px;
          margin-left: 2px;
        }

        .is-invalid {
          border-color: var(--error-color) !important;
        }
          .form-label.required::after {
          content: " *";
          color: red;
          font-weight: bold;
        }


        @media (max-width: 768px) {
          .error-text {
            font-size: 0.8rem;
          }
        }
      `}</style>
    </>
  );
};

export default Checkout;